import '../../App.css';
import { useState, useEffect } from 'react';
import React from 'react';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import * as Icon from 'react-feather';
import MainStructure from '../../components/general/MainStructure';

/* ── styles ──────────────────────────────────────────────────────────────── */
const s = {
    page: { maxWidth: 680 },
    header: { marginBottom: 28 },
    title: { fontSize: '1.45rem', fontWeight: 800, color: '#0f172a', marginBottom: 2 },
    subtitle: { fontSize: '0.9rem', color: '#64748b', margin: 0 },

    inviteCard: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 14,
        padding: '20px 22px',
        marginBottom: 28,
    },
    inviteLabel: { fontSize: '0.78rem', fontWeight: 700, color: '#64748b', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 },
    inputRow: { display: 'flex', gap: 10 },
    input: {
        flex: 1,
        border: '1px solid #d1d9e0',
        borderRadius: 9,
        padding: '9px 14px',
        fontSize: '0.9rem',
        outline: 'none',
        color: '#1e293b',
    },
    inviteBtn: {
        background: '#2563eb',
        color: '#fff',
        border: 'none',
        borderRadius: 9,
        padding: '9px 20px',
        fontWeight: 600,
        fontSize: '0.88rem',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
    },

    sectionLabel: { fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 },
    countBadge: {
        background: '#f1f5f9',
        color: '#475569',
        borderRadius: 20,
        padding: '1px 9px',
        fontSize: '0.75rem',
        fontWeight: 600,
        marginLeft: 8,
    },

    memberCard: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 12,
        padding: '13px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        marginBottom: 8,
    },
    avatar: (initial) => ({
        width: 40,
        height: 40,
        borderRadius: '50%',
        background: avatarColor(initial),
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: '0.95rem',
        flexShrink: 0,
    }),
    memberInfo: { flex: 1, minWidth: 0 },
    memberEmail: { fontSize: '0.9rem', fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    memberRole: { fontSize: '0.75rem', color: '#94a3b8', marginTop: 1 },
    badgePending: {
        fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20,
        background: '#fef3c7', color: '#92400e', letterSpacing: '0.03em',
    },
    badgeAccepted: {
        fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20,
        background: '#dcfce7', color: '#166534', letterSpacing: '0.03em',
    },
    removeBtn: {
        background: 'none',
        border: '1px solid #e2e8f0',
        borderRadius: 8,
        padding: '5px 8px',
        cursor: 'pointer',
        color: '#94a3b8',
        display: 'flex',
        alignItems: 'center',
        transition: 'all 0.15s',
        flexShrink: 0,
    },

    emptyState: {
        border: '2px dashed #e2e8f0',
        borderRadius: 14,
        padding: '36px 24px',
        textAlign: 'center',
        color: '#94a3b8',
    },
    emptyIcon: { marginBottom: 10, opacity: 0.5 },
    emptyText: { fontSize: '0.9rem', margin: 0 },

    errorBox: {
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: 9,
        padding: '10px 14px',
        fontSize: '0.85rem',
        color: '#b91c1c',
        marginBottom: 16,
    },

    /* modal */
    backdrop: {
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.45)',
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        background: '#fff',
        borderRadius: 16,
        padding: '28px 28px 24px',
        width: '100%',
        maxWidth: 380,
        boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
    },
    modalIconWrap: {
        width: 48, height: 48, borderRadius: '50%',
        background: '#fef2f2',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 16,
    },
    modalTitle: { fontSize: '1rem', fontWeight: 700, color: '#0f172a', marginBottom: 6 },
    modalBody: { fontSize: '0.875rem', color: '#64748b', lineHeight: 1.6, marginBottom: 22 },
    modalEmail: { fontWeight: 600, color: '#1e293b' },
    modalActions: { display: 'flex', gap: 10, justifyContent: 'flex-end' },
    cancelBtn: {
        background: '#f1f5f9', color: '#475569', border: 'none',
        borderRadius: 9, padding: '9px 18px', fontWeight: 600,
        fontSize: '0.875rem', cursor: 'pointer',
    },
    confirmBtn: {
        background: '#ef4444', color: '#fff', border: 'none',
        borderRadius: 9, padding: '9px 18px', fontWeight: 600,
        fontSize: '0.875rem', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: 6,
    },
};

const PALETTE = ['#3b82f6','#8b5cf6','#ec4899','#f59e0b','#10b981','#06b6d4','#f97316'];
function avatarColor(initial) {
    const idx = (initial || 'A').toUpperCase().charCodeAt(0) % PALETTE.length;
    return PALETTE[idx];
}

function generateRandomString(length = 43) {
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) result += charset[array[i] % charset.length];
    return result;
}

/* ── RemoveModal ─────────────────────────────────────────────────────────── */
function RemoveModal({ member, onConfirm, onCancel, removing }) {
    const initial = (member.user_email || 'U')[0];
    return (
        <div style={s.backdrop} onClick={onCancel}>
            <div style={s.modal} onClick={e => e.stopPropagation()}>
                <div style={s.modalIconWrap}>
                    <Icon.UserMinus size={22} color="#ef4444" />
                </div>
                <div style={s.modalTitle}>Remove team member?</div>
                <p style={s.modalBody}>
                    <span style={s.modalEmail}>{member.user_email}</span> will lose access to your account.
                    {member.status === 'pending' && ' Their pending invite will also be cancelled.'}
                    {' '}You can re-invite them at any time.
                </p>
                <div style={s.modalActions}>
                    <button style={s.cancelBtn} onClick={onCancel} disabled={removing}>
                        Cancel
                    </button>
                    <button style={s.confirmBtn} onClick={onConfirm} disabled={removing}>
                        <Icon.Trash2 size={14} />
                        {removing ? 'Removing…' : 'Remove'}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ── Team ────────────────────────────────────────────────────────────────── */
function Team() {
    const [teamMembers, setTeamMembers] = useState([]);
    const [newEmail, setNewEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [error, setError] = useState('');
    const [removeTarget, setRemoveTarget] = useState(null); // member object to remove
    const [removing, setRemoving] = useState(false);

    useEffect(() => { fetchTeam(); }, []);

    const fetchTeam = async () => {
        setLoading(true);
        try {
            const res = await fetch('https://api.schedpilot.com/wp-json/api/v1/teams/?uas=' + generateRandomString(), { credentials: 'include' });
            const data = await res.json();
            setTeamMembers(data);
        } catch {
            setError('Could not fetch team members');
        }
        setLoading(false);
    };

    const handleAddMember = async () => {
        if (!newEmail.trim()) return;
        setInviting(true);
        setError('');
        try {
            const res = await fetch('https://api.schedpilot.com/wp-json/api/v1/teams/add', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: newEmail }),
            });
            const data = await res.json();
            if (data.success) {
                setNewEmail('');
                fetchTeam();
            } else {
                setError(data.message === 'already_member' ? 'This person is already on your team.' : 'Failed to send invite.');
            }
        } catch {
            setError('Something went wrong');
        }
        setInviting(false);
    };

    const confirmRemove = async () => {
        if (!removeTarget) return;
        setRemoving(true);
        setError('');
        try {
            const res = await fetch('https://api.schedpilot.com/wp-json/api/v1/teams/remove', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_id: removeTarget.ID }),
            });
            const data = await res.json();
            if (data.success) {
                setRemoveTarget(null);
                fetchTeam();
            } else {
                setError('Failed to remove team member');
                setRemoveTarget(null);
            }
        } catch {
            setError('Something went wrong');
            setRemoveTarget(null);
        }
        setRemoving(false);
    };

    const handleKeyDown = (e) => { if (e.key === 'Enter') handleAddMember(); };

    return (
        <MainStructure activeItem={'team'}>
            <div style={s.page}>

                <div style={s.header}>
                    <h1 style={s.title}>Team</h1>
                    <p style={s.subtitle}>Invite team members to help manage your account</p>
                </div>

                {/* Invite card */}
                <div style={s.inviteCard}>
                    <p style={s.inviteLabel}>Invite a team member</p>
                    <div style={s.inputRow}>
                        <input
                            type="email"
                            placeholder="colleague@company.com"
                            style={s.input}
                            value={newEmail}
                            onChange={(e) => setNewEmail(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        <button style={s.inviteBtn} onClick={handleAddMember} disabled={inviting}>
                            <Icon.UserPlus size={15} />
                            {inviting ? 'Sending…' : 'Send invite'}
                        </button>
                    </div>
                </div>

                {error && <div style={s.errorBox}>{error}</div>}

                {/* Members list */}
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
                        <span style={s.sectionLabel}>Members</span>
                        {!loading && <span style={s.countBadge}>{teamMembers.length}</span>}
                    </div>

                    {loading ? (
                        <LoadingSpinner />
                    ) : teamMembers.length === 0 ? (
                        <div style={s.emptyState}>
                            <div style={s.emptyIcon}><Icon.Users size={36} /></div>
                            <p style={s.emptyText}>No team members yet. Send an invite above to get started.</p>
                        </div>
                    ) : (
                        teamMembers.map((member) => {
                            const initial = (member.user_email || 'U')[0];
                            const isPending = member.status === 'pending';
                            return (
                                <div style={s.memberCard} key={member.ID}>
                                    <div style={s.avatar(initial)}>{initial.toUpperCase()}</div>
                                    <div style={s.memberInfo}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div style={s.memberEmail}>{member.user_email}</div>
                                            <span style={isPending ? s.badgePending : s.badgeAccepted}>
                                                {isPending ? 'Pending' : 'Accepted'}
                                            </span>
                                        </div>
                                        <div style={s.memberRole}>
                                            {isPending ? 'Invite sent — awaiting acceptance' : 'Team member'}
                                        </div>
                                    </div>
                                    <button
                                        style={s.removeBtn}
                                        title="Remove member"
                                        onClick={() => setRemoveTarget(member)}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#ef4444'; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.color = '#94a3b8'; }}
                                    >
                                        <Icon.Trash2 size={15} />
                                    </button>
                                </div>
                            );
                        })
                    )}
                </div>

            </div>

            {removeTarget && (
                <RemoveModal
                    member={removeTarget}
                    onConfirm={confirmRemove}
                    onCancel={() => setRemoveTarget(null)}
                    removing={removing}
                />
            )}

        </MainStructure>
    );
}

export default Team;
